import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomerSummary } from '../services'
import { CustomerPipelineFilters } from '../types'
import { Users, Briefcase, Server, Tag, TrendingUp, Info } from 'lucide-react'
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import dayjs from 'dayjs'

interface CustomerSummaryCardsProps {
    filters: CustomerPipelineFilters
}

type ColorVariant = 'blue' | 'emerald' | 'violet' | 'amber'

interface StatCardProps {
    title: string
    value: string | number
    subtitle?: string
    subtitleValue?: string | number
    icon: React.ReactNode
    trend?: number | null
    tooltip?: string
    variant?: ColorVariant
}

const variantStyles: Record<
    ColorVariant,
    { iconBg: string; iconColor: string; accent: string }
> = {
    blue: {
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        accent: 'text-blue-600',
    },
    emerald: {
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        accent: 'text-emerald-600',
    },
    violet: {
        iconBg: 'bg-violet-50',
        iconColor: 'text-violet-600',
        accent: 'text-violet-600',
    },
    amber: {
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        accent: 'text-amber-600',
    },
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    subtitle,
    subtitleValue,
    icon,
    trend,
    tooltip,
    variant = 'blue',
}) => {
    const styles = variantStyles[variant]

    return (
        <div className="bg-white rounded-md px-3 py-2.5 border border-gray-100 shadow-sm hover:shadow transition-shadow">
            <div className="flex items-center gap-2.5">
                <div
                    className={`${styles.iconBg} ${styles.iconColor} p-2 rounded-md shrink-0`}
                >
                    {icon}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">
                            {title}
                        </p>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button className="text-gray-300 hover:text-gray-500 transition-colors shrink-0">
                                        <Info className="w-3 h-3" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="max-w-xs text-xs">
                                        {tooltip || title}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <span className="font-bold text-primary truncate capitalize">
                            {typeof value === 'number'
                                ? value.toLocaleString()
                                : (value as string)?.toLowerCase()}
                        </span>
                        {trend !== undefined && trend !== null && (
                            <span
                                className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium ${
                                    trend >= 0
                                        ? 'bg-green-50 text-green-700'
                                        : 'bg-red-50 text-red-600'
                                }`}
                            >
                                <TrendingUp
                                    className={`w-2.5 h-2.5 ${
                                        trend < 0 ? 'rotate-180' : ''
                                    }`}
                                />
                                {Math.abs(trend).toFixed(1)}%
                            </span>
                        )}
                        {subtitleValue !== undefined && (
                            <span
                                className={`text-xs font-semibold ${styles.accent}`}
                            >
                                {subtitleValue}
                                {subtitle && (
                                    <span className="font-normal text-gray-400 ml-0.5">
                                        {subtitle}
                                    </span>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

/** Calculate growth rate between current and previous values */
const calcGrowth = (current?: number, previous?: number): number | null => {
    if (!current || !previous || previous === 0) return null
    return ((current - previous) / previous) * 100
}

const CustomerSummaryCards: React.FC<CustomerSummaryCardsProps> = ({
    filters,
}) => {
    const { t } = useTranslation()

    // Calculate previous period filters (same duration, shifted back)
    const previousFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null

        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const diffDays = end.diff(start, 'day') + 1

        return {
            ...filters,
            gte: start.subtract(diffDays, 'day').format('YYYY-MM-DD'),
            lte: start.subtract(1, 'day').format('YYYY-MM-DD'),
        }
    }, [filters])

    // Current period query
    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'customer-summary',
                filters.gte,
                filters.lte,
                filters.sales_manager_codes,
            ],
            queryFn: () => getCustomerSummary(filters),
            enabled: !!filters.gte && !!filters.lte,
        },
        {
            loadingComponent: (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((i) => (
                        <div
                            key={i}
                            className="bg-white rounded-md px-3 py-2.5 border border-gray-100 shadow-sm animate-pulse"
                        >
                            <div className="flex items-center gap-2.5">
                                <div className="h-8 w-8 bg-gray-200 rounded-md shrink-0" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-2.5 bg-gray-100 rounded w-16" />
                                    <div className="h-5 bg-gray-200 rounded w-12" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ),
        }
    )

    // Previous period query for comparison
    const { data: previousData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'customer-summary',
                previousFilters?.gte,
                previousFilters?.lte,
                previousFilters?.sales_manager_codes,
            ],
            queryFn: () => getCustomerSummary(previousFilters!),
            enabled: !!previousFilters?.gte && !!previousFilters?.lte && !!data, // Only fetch after current data loaded
        },
        {}
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const topIndustry = data?.top_industries?.[0]
    const topProduct = data?.top_products?.[0]
    const topCategory = data?.top_expectation_categories?.[0]

    // Previous period data
    const prevTopIndustry = previousData?.top_industries?.find(
        (i) => i.name === topIndustry?.name
    )
    const prevTopProduct = previousData?.top_products?.find(
        (p) => p.name === topProduct?.name
    )

    // Calculate growth rates
    const totalGrowth = calcGrowth(
        data?.total_contacts,
        previousData?.total_contacts
    )
    const industryGrowth = calcGrowth(
        topIndustry?.count,
        prevTopIndustry?.count
    )
    const productGrowth = calcGrowth(topProduct?.count, prevTopProduct?.count)

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <StatCard
                title={t('customer_summary.total_customers')}
                value={data?.total_contacts || 0}
                icon={<Users className="w-4 h-4" />}
                trend={totalGrowth}
                tooltip={t('customer_summary.total_customers_tooltip')}
                variant="blue"
            />

            <StatCard
                title={t('customer_summary.top_industry')}
                value={topIndustry?.name || '--'}
                subtitle={t('customer_summary.customers')}
                subtitleValue={topIndustry?.count}
                trend={industryGrowth}
                icon={<Briefcase className="w-4 h-4" />}
                tooltip={t('customer_summary.top_industry_tooltip')}
                variant="emerald"
            />

            <StatCard
                title={t('customer_summary.top_service')}
                value={topProduct?.name || '--'}
                subtitleValue={topProduct?.count}
                trend={productGrowth}
                icon={<Server className="w-4 h-4" />}
                tooltip={t('customer_summary.top_service_tooltip')}
                variant="violet"
            />

            <StatCard
                title={t('customer_summary.top_category')}
                value={topCategory?.name || '--'}
                subtitle=""
                subtitleValue={topCategory?.count ? `--` : undefined}
                icon={<Tag className="w-4 h-4" />}
                tooltip={t('customer_summary.top_category_tooltip')}
                variant="amber"
            />
        </div>
    )
}

export default CustomerSummaryCards
